// User and Organization Types
export interface User {
  id: string;
  email: string;
  username: string;
  is_active: boolean;
  role: 'super_admin' | 'organization_admin' | 'organization_user';
  organization_id?: string;
  organization?: Organization;
  created_at: string;
  updated_at?: string;
}

export interface Organization {
  id: string;
  name: string;
  description?: string;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
  user_count?: number;
  application_count?: number;
}

export interface Feature {
  id: string;
  name: string;
  feature_code: string;
  description?: string;
  is_consumable: boolean;
  application_id: string;
  created_at: string;
  updated_at?: string;
}

export interface PricingTier {
  amount: number;
  currency: string;
  duration: string;
}

export interface Plan {
  id: string;
  name: string;
  description?: string;
  pricing_tiers?: PricingTier[];
  is_active: boolean;
  application_id: string;
  selected_features?: string[];
  consumable_counts?: Record<string, number>;
  display_order?: number;
  created_at: string;
  updated_at?: string;
}

export interface Application {
  id: string;
  name: string;
  description?: string;
  logo_url?: string;
  callback_url?: string;
  redirect_url?: string;
  is_active: boolean;
  organization_id: string;
  created_at: string;
  updated_at?: string;
  features?: Feature[];
  plans?: Plan[];
}

// API Key interfaces
export interface APIKey {
  id: string;
  name: string;
  key_prefix: string;
  is_active: boolean;
  last_used_at?: string;
  expires_at?: string;
  organization_id: string;
  created_by_id: string;
  created_at: string;
  updated_at?: string;
}

export interface APIKeyFormData {
  name: string;
  expires_at?: string;
}

export interface APIKeyCreateResponse extends APIKey {
  key: string; // Only returned when creating a new key
}

export interface Purchase {
  id: string;
  transaction_id: string;
  user_id: string;
  user_email: string;
  user_name?: string;
  plan_id: string;
  application_id: string;
  amount?: string;
  currency?: string;
  duration?: string;
  start_date?: string;
  end_date?: string;
  status: string;
  callback_sent: boolean;
  purchased_plan_id?: string;
  created_at: string;
  updated_at?: string;
  plan?: {
    id: string;
    name: string;
    description?: string;
  };
  purchased_plan?: {
    id: string;
    original_plan_id: string;
    name: string;
    description?: string;
    pricing_tiers?: any[];
    selected_features?: string[];
    consumable_counts?: any;
    is_active: boolean;
    original_created_at?: string;
    snapshot_taken_at: string;
    created_at: string;
    updated_at?: string;
  };
  application?: {
    id: string;
    name: string;
    description?: string;
    logo_url?: string;
  };
}

export interface ApplicationAnalytics {
  application_id: string;
  application_name: string;
  total_purchases: number;
  month_purchases: number;
  total_revenue: number;
  month_revenue: number;
  currency: string;
}

// API Response Types
export interface LoginResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
}

export interface OrganizationWithApps {
  id: string;
  name: string;
  application_count: number;
}

export interface OrganizationDashboardStats {
  total_applications: number;
  total_plans: number;
  total_revenue: number;
  month_revenue: number;
  total_subscriptions: number;
  month_subscriptions: number;
}

export interface SuperAdminDashboardStats {
  total_organizations: number;
  total_applications: number;
  total_subscriptions: number;
  total_revenue: number;
  month_revenue: number;
}

export interface DashboardStats {
  total_users: number;
  active_users: number;
  super_admins: number;
  total_organizations: number;
  total_applications: number;
  organizations_with_apps: OrganizationWithApps[];
  organization_stats?: OrganizationDashboardStats;
  super_admin_stats?: SuperAdminDashboardStats;
}

export interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signup: (email: string, username: string, password: string) => Promise<{ success: boolean; error?: string }>;
  logout: () => void;
  token: string | undefined;
}

// Form Data Types
export interface LoginFormData {
  email: string;
  password: string;
}

export interface SignupFormData {
  email: string;
  username: string;
  password: string;
  confirmPassword: string;
}

export interface UserFormData {
  email: string;
  username: string;
  password?: string;
  organization_id?: string;
  role: 'super_admin' | 'organization_admin' | 'organization_user';
}

export interface OrganizationFormData {
  name: string;
  description?: string;
}

export interface FeatureFormData {
  name: string;
  feature_code: string;
  description?: string;
  is_consumable: boolean;
}

export interface PlanFormData {
  name: string;
  description?: string;
  pricing_tiers?: PricingTier[];
  selected_features?: string[];
  consumable_counts?: Record<string, number>;
}

export interface ApplicationFormData {
  name: string;
  description?: string;
  logo_url?: string;
  callback_url?: string;
  redirect_url?: string;
  organization_id?: number;
  features?: FeatureFormData[];
}

export interface ProfileFormData {
  username: string;
  email: string;
}

export interface PasswordFormData {
  currentPassword: string;
  newPassword: string;
  confirmPassword: string;
}

// Component Props Types
export interface ProtectedRouteProps {
  children: React.ReactNode;
}

export interface LayoutProps {
  children: React.ReactNode;
}

// API Error Response
export interface ValidationError {
  type: string;
  loc: string[];
  msg: string;
  input: any;
  url: string;
}

export interface ApiError {
  detail: string | ValidationError[];
}

// Helper function to format error messages
export const formatErrorMessage = (error: any, fallback: string = 'An error occurred'): string => {
  if (typeof error === 'string') {
    return error;
  }
  
  if (error?.response?.data?.detail) {
    const detail = error.response.data.detail;
    
    if (typeof detail === 'string') {
      return detail;
    }
    
    if (Array.isArray(detail)) {
      return detail.map((err: ValidationError) => `${err.loc.join('.')}: ${err.msg}`).join(', ');
    }
  }
  
  return fallback;
};
